import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/CaseStudies.css';

const caseStudies = [
  {
    title: 'Modernizing Compliance & Reporting with Data at Scale',
    sector: 'Banking',
    capability: 'Data & AI',
    summary: 'A leading bank modernized legacy reporting by consolidating systems into a governed data platform, reducing manual reconciliation and giving executives near real-time visibility.',
    metric: { value: '35', emphasis: '%', label: 'Faster reporting cycles' },
    duration: '9 months',
    outcome: '100% compliance',
  },
  {
    title: 'Building a Trusted Data Foundation for Financial Services',
    sector: 'Financial Services',
    capability: 'Data & AI',
    summary: 'A financial services leader built a governance-first data ecosystem, lowering integration costs and eliminating audit findings with a trusted enterprise data foundation.',
    metric: { value: '0', emphasis: '', label: 'Audit findings under NDMO' },
    duration: '14 months',
    outcome: 'NDMO compliant',
  },
  {
    title: 'Unified Data for Smarter Insurance Decisions',
    sector: 'Insurance',
    capability: 'Applied Intelligence',
    summary: 'A top insurer unified disconnected data silos into a consistent platform, enabling faster reporting, clearer customer visibility, and more accurate underwriting decisions.',
    metric: { value: '1200', emphasis: '+', label: 'Attributes unified' },
    duration: '16 months',
    outcome: '40% faster reporting',
  },
  {
    title: 'A Single Customer View for Better Airline Experiences',
    sector: 'Airlines',
    capability: 'Engineering',
    summary: 'A national airline created a unified passenger profile, improving compliance, reducing duplicate records, and enabling a more consistent customer experience.',
    metric: { value: '30', emphasis: '%', label: 'Duplicate records reduced' },
    duration: '12 months',
    outcome: 'Stronger compliance',
  },
];

const pageStats = [
  { label: 'Engagements', value: '15+' },
  { label: 'Sectors served', value: '8' },
  { label: 'On-time delivery', value: '95%' },
  { label: 'Client retention', value: '92%' },
];

const sectorCards = [
  { index: "01", name: "Government", meta: "Authorities & entities", category: "Public Sector" },
  { index: "02", name: "Banking", meta: "Local & International", category: "Finance" },
  { index: "03", name: "Telecom", meta: "Operators & providers", category: "Comms" },
  { index: "04", name: "Energy & Chemicals", meta: "Strategic sector", category: "Energy" },
  { index: "05", name: "Healthcare", meta: "Public & private", category: "Health" },
  { index: "06", name: "Retail & Consumer", meta: "Regional & global brands", category: "Retail" },
  { index: "07", name: "Insurance", meta: "Regulated entities", category: "Insurance" },
  { index: "08", name: "Manufacture", meta: "Industrial enterprises", category: "Industrial" }
];

const CaseStudies = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Case Studies | Intellient Solutions';
  }, []);

  return (
    <main className="main-cs-page">
      <section className="main-cs-hero">

        <div className="main-cs-hero-inner">
          <nav className="svc-breadcrumb">
            <a href="/" onClick={() => navigate('/')} className="svc-breadcrumb-link">Home</a>
            <span className="svc-breadcrumb-sep">/</span>
            <span className="svc-breadcrumb-current">Case Studies</span>
          </nav>
          <h1>Outcomes worth measuring.</h1>
          <p>Delivering enterprise-grade data, AI and cloud outcomes for banks, insurers, government entities, and regulators across the region.</p>
          <div className="main-cs-stats">
            {pageStats.map((stat) => (
              <div key={stat.label} className="main-cs-stat">
                <span className="main-cs-stat-value">{stat.value}</span>
                <span className="main-cs-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="main-cs-featured">
        <div className="main-cs-featured-card">
          <div className="main-cs-featured-visual">
            <div className="main-cs-featured-header">
              <span className="main-cs-featured-badge">Featured Case Study</span>
              <div className="main-cs-featured-client">A Tier-1 Bank in the Region</div>
              <div className="main-cs-featured-sector">Banking · Data Platform</div>
            </div>
            <div className="main-cs-featured-number">60<em>%</em></div>
            <p className="main-cs-featured-caption">Faster regulatory reporting through a governed data platform unifying 200+ data sources.</p>
          </div>
          <div className="main-cs-featured-copy">
            <span className="main-cs-featured-eyebrow">Banking · 18-month engagement</span>
            <h2>From compliance burden to competitive advantage.</h2>
            <p>We reimagined legacy reporting and built a modern data platform that delivered audit-ready reporting, real-time executive visibility, and full regulatory alignment.</p>
            <div className="main-cs-featured-metrics">
              <div>
                <strong>18 mo</strong>
                <span>Design to deployment</span>
              </div>
              <div>
                <strong>200+</strong>
                <span>Data sources unified</span>
              </div>
              <div>
                <strong>100%</strong>
                <span>SAMA compliance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="main-cs-grid-section" id="case-study-list">
        <div className="main-cs-grid-header">
          <span className="main-cs-eyebrow">Case Studies</span>
          <h2>Four standout engagements in one place.</h2>
        </div>
        <div className="main-cs-grid">
          {caseStudies.map((study) => (
            <article key={study.title} className="main-cs-card">
              <div className="main-cs-card-media">
                <div className="main-cs-card-tag">
                  <span>{study.sector}</span>
                  <strong>{study.capability}</strong>
                </div>
                <div className="main-cs-card-visual">
                  <span className="main-cs-bigstat">
                    {study.metric.value}
                    {study.metric.emphasis && <em>{study.metric.emphasis}</em>}
                  </span>
                  <p>{study.metric.label}</p>
                </div>
              </div>
              <div className="main-cs-card-body">
                <h3>{study.title}</h3>
                <p>{study.summary}</p>
                <div className="main-cs-card-meta">
                  <div>
                    <span>Duration</span>
                    <strong>{study.duration}</strong>
                  </div>
                  <div>
                    <span>Outcome</span>
                    <strong>{study.outcome}</strong>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="main-cs-testimonial">
        <div className="main-cs-testimonial-inner">
          <span className="main-cs-testimonial-mark">“</span>
          <p>Intellient delivered exactly what they promised — on time, on budget, and with a level of <em>care</em> we hadn't seen from larger consultancies.</p>
          <div className="main-cs-testimonial-by">
            <span>Chief Data Officer · Banking Sector</span>
            <strong>KSA</strong>
          </div>
        </div>
      </section>

      <section className="main-cs-sectors">
        <div className="main-cs-grid-header">
          <span className="main-cs-eyebrow">Where We’ve Worked</span>
          <h2>Depth across every regulated sector.</h2>
        </div>
        <div className="main-cs-sector-grid">
          {sectorCards.map((item) => (
            <div key={item.name} className="main-cs-sector-card">
              <div className="main-cs-sector-index">{item.index} / {item.category}</div>
              <div className="main-cs-sector-name">{item.name}</div>
              <div className="main-cs-sector-meta">{item.meta}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="cs-cta" id="contact">
        <div className="cs-container">
          <div className="cs-cta-inner">
            <div className="cs-cta-content">
              <div className="cs-cta-eyebrow">Start a Conversation</div>
              <h2>Let's build something that lasts.</h2>
              <p>Every great engagement begins with a conversation. Tell us about your ambition — we'll tell you how we can help.</p>
              <button onClick={() => navigate("/contact")} type="submit" className="cs-btn-cta-primary">Request a Meeting <span>→</span></button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CaseStudies;
